import { Fragment, createElement, type ReactNode } from "react";

import { t, type TranslateParams } from "./catalogue.js";

/**
 * A translated sentence with React nodes substituted into it.
 *
 * ==========================================================================================
 * WHY `t()` ALONE IS NOT ENOUGH, AND WHY CONCATENATION IS THE WRONG ANSWER
 * ==========================================================================================
 *
 * Several sentences in this application wrap a figure that is EMPHASISED:
 *
 *     The household is committed to <strong>€1,400.00</strong> per month.
 *
 * `t()` returns a string, so the emphasis cannot come from the catalogue. The obvious workaround is
 * to cut the sentence into two entries — `"The household is committed to "` and `" per month."` —
 * and that is the mistake this helper exists to avoid. It bakes ENGLISH WORD ORDER into the
 * component: a language that puts the figure elsewhere in the sentence cannot be written at all, and
 * a translator is handed two fragments neither of which is a sentence.
 *
 * Feature 021 met this the hard way. A bulk pass replaced the leading fragment and left the trailing
 * one behind, so a Dutch reader got "Dat is €1.400,00 per month." — which is worse than no
 * translation, because it looks like a bug in the product rather than an unfinished job.
 *
 * ==========================================================================================
 * HOW IT WORKS
 * ==========================================================================================
 *
 * The catalogue entry holds the WHOLE sentence with named placeholders:
 *
 *     "exp.list.committed": "The household is committed to {amount} per month."
 *
 * and the caller supplies what each placeholder becomes:
 *
 *     tRich("exp.list.committed", { amount: <strong>{formatEur(total)}</strong> })
 *
 * The Dutch entry is then free to put `{amount}` wherever Dutch puts it, and the emphasis travels
 * with it. Plural selection and plain-text interpolation still work: `params` is passed through to
 * `t`, so `{count}` can pick the form and `{name}` can substitute a plain string, with `nodes`
 * handling only the parts that need to be elements.
 */
export function tRich(
  id: string,
  nodes: Readonly<Record<string, ReactNode>>,
  params?: TranslateParams,
): ReactNode {
  const sentence = t(id, params);

  /*
   * Split KEEPING the delimiters, so the placeholder names survive to be looked up. An unknown
   * placeholder is left as written — the same rule `interpolate` follows, and for the same reason:
   * a visible `{amount}` is a bug report and an empty gap is a mystery.
   */
  const parts = sentence.split(/(\{\w+\})/g);

  return createElement(
    Fragment,
    null,
    ...parts.map((part, index) => {
      const name = /^\{(\w+)\}$/.exec(part)?.[1];
      const node = name === undefined ? part : (nodes[name] ?? part);

      // Keyed, because these are array children and React asks. The index is stable here: the
      // sentence is fixed for a given language, so the parts do not reorder between renders.
      return createElement(Fragment, { key: `${index}` }, node);
    }),
  );
}
